'use client';

import React from 'react';
import { Stack, Container, Grid, Typography, Box } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import theme, { blueGradientSxDown } from "@/theme/theme";
import Footer from "@/components/layout/footer";
import AboutHero from "@/sections/about/aboutHero";
import menuTeam from "@/assets/images/icons/menu-team.webp";
import menuPortfolio from "@/assets/images/icons/menu-portfolio.webp";

export default function AboutUsPage() {
    return (
        <Stack component="main" width="100%" alignItems="center" sx={{ bgcolor: '#0B0B0B' }}>
            <AboutHero />
            <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
                <Stack gap={4} alignItems="center" textAlign="center">
                    <Typography variant="h4" fontWeight={400} sx={{ color: 'text.primary' }}>
                        Shaping the future of Web3 and AI
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, fontWeight: 300 }}>
                        At Mood Global Services, we don&apos;t just write code; we architect solutions. 
                        Whether it&apos;s designing complex Smart Contracts, deploying robust Layer 2 infrastructures, 
                        or integrating cutting-edge AI Systems, our team operates at the intersection of innovation and reliability. 
                        We believe in a tailored approach, ensuring every line of code serves your overarching business goals.
                    </Typography>
                </Stack>
            </Container>
            <Container maxWidth="lg" sx={{ pb: { xs: 12, md: 16 } }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Link href="/team" style={{ textDecoration: 'none' }}>
                            <CardWrapper>
                                <Typography variant="subtitle1" color={theme.palette.brand.mgsPurple1.main} sx={{ mb: 4 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            ...blueGradientSxDown(theme),
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                        }}
                                    >
                                        Team
                                    </Box>
                                </Typography>
                                <Stack flexGrow={1} alignItems="center" justifyContent="center">
                                    <Image src={menuTeam} width={150} height={75} alt="Our Team" />
                                </Stack>
                                <CardButton>View</CardButton>
                            </CardWrapper>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Link href="/vacancies" style={{ textDecoration: 'none' }}>
                            <CardWrapper>
                                <Typography variant="subtitle1" sx={{ mb: 4 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            ...blueGradientSxDown(theme),
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                        }}
                                    >
                                        Careers
                                    </Box>
                                </Typography>
                                <Stack flexGrow={1} alignItems="center" justifyContent="center">
                                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: "200px", lineHeight: 1.6, fontWeight: 300 }}>
                                        Check out our open roles and become part of a team that&apos;s shaping the future
                                    </Typography>
                                </Stack>
                                <CardButton>Join the team</CardButton>
                            </CardWrapper>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Link href="/portfolio" style={{ textDecoration: 'none' }}>
                            <CardWrapper>
                                <Typography variant="subtitle1" color={theme.palette.brand.mgsPurple1.main} sx={{ mb: 4 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            ...blueGradientSxDown(theme),
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                        }}
                                    >
                                        Portfolio
                                    </Box>
                                </Typography>
                                <Stack flexGrow={1} alignItems="center" justifyContent="center">
                                    <Image src={menuPortfolio} width={150} height={75} alt="Our Portfolio" />
                                </Stack>
                                <CardButton>View</CardButton>
                            </CardWrapper>
                        </Link>
                    </Grid>
                </Grid>
            </Container>
            <Stack width="100%" sx={{ borderTop: `1px solid ${theme.palette.brand.border1.main}` }}>
                <Footer />
            </Stack>
        </Stack>
    );
}

function CardWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Stack 
            height="100%" 
            minHeight="280px"
            p={3} 
            sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.02)', 
                border: `1px solid ${theme.palette.brand.border1.main}`,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                    transform: 'translateY(-4px)'
                }
            }}
        >
            {children}
        </Stack>
    );
}

function CardButton({ children }: { children: React.ReactNode }) {
    return (
        <Stack 
            direction="row" 
            alignItems="center" 
            justifyContent="center" 
            sx={{ 
                mt: 4,
                py: 1.5, 
                bgcolor: '#1D1D1D', 
                borderRadius: 1,
                boxShadow: '0 4px 0 #292929',
                transition: 'all 0.1s ease',
                '&:active': {
                    transform: 'translateY(4px)',
                    boxShadow: '0 0px 0 #292929',
                }
            }}
        >
            <Typography variant="subtitle2" sx={{ color: 'text.primary', whiteSpace: "nowrap" }}>
                {children}
            </Typography>
        </Stack>
    );
}